import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import { store } from './store/store';
import { setCurrentService, fetchCategoriesStatus } from './store/slices/servicesSlice';
import { resetCheckout } from './store/slices/orderSlice';
import Layout from './components/common/Layout';
import ServiceSelection from './components/common/ServiceSelection';
import LoadingSpinner from './components/common/LoadingSpinner';
import PerformanceProvider from './components/common/PerformanceProvider';
import './index.css';

// Lazy load service components for better performance
const CloudVPSForm = lazy(() => import('./components/services/CloudVPSForm'));
const OrchestratorForm = lazy(() => import('./components/services/OrchestratorForm'));
const DBaaSForm = lazy(() => import('./components/services/DBaaSForm'));
const DomainsForm = lazy(() => import('./components/services/DomainsForm'));
const CMSaaSForm = lazy(() => import('./components/services/CMSaaSForm'));
const DedicatedServerForm = lazy(() => import('./components/services/DedicatedServerForm'));
const CheckoutForm = lazy(() => import('./components/checkout/CheckoutForm'));

// Lazy load DNS components
const DNSAuthEmail = lazy(() => import('./components/dns/DNSAuthEmail'));
const DNSAuthOTP = lazy(() => import('./components/dns/DNSAuthOTP'));
const DNSPanel = lazy(() => import('./components/dns/DNSPanel'));
const DNSManage = lazy(() => import('./components/dns/DNSManage'));

// Lazy load Dashboard components
const ResourcesPanel = lazy(() => import('./components/dashboard/ResourcesPanel'));
const ResourceAuthEmail = lazy(() => import('./components/dashboard/ResourceAuthEmail'));
const ResourceAuthOTP = lazy(() => import('./components/dashboard/ResourceAuthOTP'));

const AppContent = () => {
  const dispatch = useDispatch();
  const [currentView, setCurrentView] = useState('services'); // services, service-form, checkout
  const [selectedService, setSelectedService] = useState(null);

  // Charger les statuts des catégories au démarrage
  useEffect(() => {
    dispatch(fetchCategoriesStatus());
    
    // Rafraîchir toutes les 10 secondes (l'overlay ne s'affiche qu'au premier chargement)
    const intervalId = setInterval(() => {
      dispatch(fetchCategoriesStatus());
    }, 10000); // 10000ms = 10 secondes

    return () => clearInterval(intervalId);
  }, [dispatch]);

  const handleServiceSelect = (serviceId) => {
    // Clear forms when switching services
    dispatch(setCurrentService(serviceId));
    dispatch(resetCheckout());
    setSelectedService(serviceId);
    setCurrentView('service-form');
    
    // Auto-scroll to center the form after selection
    setTimeout(() => {
      window.scrollTo({
        top: window.innerHeight * 0.3, // Scroll to 30% of viewport height
        behavior: 'smooth'
      });
    }, 100);
  };

  const handleServiceFormNext = () => {
    setCurrentView('checkout');
    
    // Auto-scroll to center the checkout form
    setTimeout(() => {
      window.scrollTo({
        top: window.innerHeight * 0.2,
        behavior: 'smooth'
      });
    }, 100);
  };

  const handleCheckoutComplete = () => {
    setCurrentView('services');
    setSelectedService(null);
    
    // Auto-scroll to top when returning to services
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 100);
  };

  const handleBackToServices = () => {
    // Clear all forms when going back to services
    dispatch(setCurrentService(null));
    dispatch(resetCheckout());
    setCurrentView('services');
    setSelectedService(null);
    
    // Auto-scroll to top when returning to services
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 100);
  };

  const renderServiceForm = () => {
    switch (selectedService) {
      case 'cloudVPS':
        return (
          <Suspense fallback={<LoadingSpinner text="Chargement du formulaire Cloud VPS..." />}>
            <CloudVPSForm onNext={handleServiceFormNext} />
          </Suspense>
        );
      case 'orchestrator':
        return (
          <Suspense fallback={<LoadingSpinner text="Chargement du formulaire Orchestrateur..." />}>
            <OrchestratorForm onNext={handleServiceFormNext} />
          </Suspense>
        );
      case 'dbaas':
        return (
          <Suspense fallback={<LoadingSpinner text="Chargement du formulaire DBaaS..." />}>
            <DBaaSForm onNext={handleServiceFormNext} />
          </Suspense>
        );
      case 'domains':
        return (
          <Suspense fallback={<LoadingSpinner text="Chargement du formulaire Domaines..." />}>
            <DomainsForm onNext={handleServiceFormNext} />
          </Suspense>
        );
      case 'cmsaas':
        return (
          <Suspense fallback={<LoadingSpinner text="Chargement du formulaire CMSaaS..." />}>
            <CMSaaSForm onNext={handleServiceFormNext} />
          </Suspense>
        );
      case 'dedicatedServer':
        return (
          <Suspense fallback={<LoadingSpinner text="Chargement du formulaire Serveur Dédié..." />}>
            <DedicatedServerForm onNext={handleServiceFormNext} />
          </Suspense>
        );
      default:
        return <ServiceSelection onServiceSelect={handleServiceSelect} />;
    }
  };

  const renderContent = () => {
    switch (currentView) {
      case 'services':
        return <ServiceSelection onServiceSelect={handleServiceSelect} />;
      case 'service-form':
        return (
          <div className="space-y-6">
            <button
              onClick={handleBackToServices}
              className="btn-outline flex items-center space-x-2"
            >
              <span>←</span>
              <span>Retour aux services</span>
            </button>
            {renderServiceForm()}
          </div>
        );
      case 'checkout':
        return (
          <div className="space-y-6">
            <button
              onClick={() => setCurrentView('service-form')}
              className="btn-outline flex items-center space-x-2"
            >
              <span>←</span>
              <span>Retour à la configuration</span>
            </button>
            <Suspense fallback={<LoadingSpinner text="Chargement du checkout..." />}>
              <CheckoutForm onComplete={handleCheckoutComplete} />
            </Suspense>
          </div>
        );
      default:
        return <ServiceSelection onServiceSelect={handleServiceSelect} />;
    }
  };

  return (
    <Layout 
      currentService={selectedService} 
      onServiceSelect={handleServiceSelect}
    >
      {renderContent()}
    </Layout>
  );
};

const App = () => {
  return (
    <Provider store={store}>
      <PerformanceProvider>
        <BrowserRouter>
          <Routes>
            {/* Main App Routes */}
            <Route path="/" element={<AppContent />} />
            
            {/* DNS Panel Routes */}
            <Route path="/dns/auth" element={
              <Suspense fallback={<LoadingSpinner text="Chargement..." />}>
                <DNSAuthEmail />
              </Suspense>
            } />
            <Route path="/dns/auth/otp" element={
              <Suspense fallback={<LoadingSpinner text="Chargement..." />}>
                <DNSAuthOTP />
              </Suspense>
            } />
            <Route path="/dns/panel" element={
              <Suspense fallback={<LoadingSpinner text="Chargement..." />}>
                <DNSPanel />
              </Suspense>
            } />
            <Route path="/dns/manage/:domain" element={
              <Suspense fallback={<LoadingSpinner text="Chargement..." />}>
                <DNSManage />
              </Suspense>
            } />
            
            {/* Resources Panel Routes */}
            <Route path="/resources/auth" element={
              <Suspense fallback={<LoadingSpinner text="Chargement..." />}>
                <ResourceAuthEmail />
              </Suspense>
            } />
            <Route path="/resources/auth/otp" element={
              <Suspense fallback={<LoadingSpinner text="Chargement..." />}>
                <ResourceAuthOTP />
              </Suspense>
            } />
            <Route path="/resources/panel" element={
              <Suspense fallback={<LoadingSpinner text="Chargement du panneau de ressources..." />}>
                <ResourcesPanel />
              </Suspense>
            } />
            {/* Redirect /resources to /resources/auth */}
            <Route path="/resources" element={
              <Suspense fallback={<LoadingSpinner text="Chargement..." />}>
                <ResourceAuthEmail />
              </Suspense>
            } />
          </Routes>
        </BrowserRouter>
      </PerformanceProvider>
    </Provider>
  );
};

export default App;
